import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiLock, FiFileText, FiSlash } from 'react-icons/fi';
import Dialog from '../../../organisms/Dialog';
import DateField from '../../../molecules/DateField';
import TextEdit from '../../../molecules/TextEdit';
import { HelpButton } from './SectionHelp';
import downloadCsv from './downloadCsv';
import { buildSnapshotCsv, snapshotCsvFilename } from './cpc25Export';
import { recoveryApi, ApiError } from '../../../../services/recoveryApi';
import type { ProvisionSnapshotDetail, ProvisionSnapshotHeader } from '../../../../types/LegalCase';

// Modal de fechamento contábil do CPC 25 (Task 20). Fecha o período numa data-base escolhida,
// congelando todos os casos num snapshot imutável — reexportar o mesmo período devolve sempre o
// mesmo número, mesmo que a classificação de um caso mude depois. Restrito ao jurídico
// (ADMIN/LEGAL) no backend, que responde 403 para os demais. Lista os fechamentos já feitos.

interface ProvisionPeriodModalProps {
    onClose: () => void;
    onClosed: () => void;
}

const fmtDate = (iso: string | null): string => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');

// Hoje em ISO (yyyy-mm-dd), para travar a data-base no `max` do campo. O backend recusa futuro com
// 422; o `max` evita o usuário descobrir isso só depois de clicar.
const todayIso = (): string => new Date().toISOString().slice(0, 10);

const ProvisionPeriodModal = ({ onClose, onClosed }: ProvisionPeriodModalProps) => {
    // Default: hoje. O uso real é "fechar o período que terminou", então a data corrente é o
    // palpite certo na maioria das vezes.
    const [baseDate, setBaseDate] = useState(todayIso());
    const [label, setLabel] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [snapshots, setSnapshots] = useState<ProvisionSnapshotHeader[]>([]);
    // Anulação: qual fechamento está sendo anulado e a justificativa digitada.
    const [voidingId, setVoidingId] = useState<string | null>(null);
    const [voidReason, setVoidReason] = useState('');
    // Export de um fechamento: qual está sendo baixado (desabilita o botão durante a busca).
    const [exportingId, setExportingId] = useState<string | null>(null);

    const loadSnapshots = () => {
        recoveryApi.getProvisionSnapshots()
            .then((result) => setSnapshots(result?.snapshots || []))
            .catch(() => { /* silencioso — a lista fica vazia se falhar */ });
    };

    useEffect(() => { loadSnapshots(); }, []);

    const handleClosePeriod = async () => {
        if (!baseDate) {
            toast.error('Informe a data-base do fechamento');
            return;
        }
        // Retorno rápido para data futura; o backend recusa igual (422) e segue sendo a autoridade.
        if (baseDate > todayIso()) {
            toast.error('A data-base não pode ser futura: o período ainda não terminou');
            return;
        }
        setIsSaving(true);
        try {
            await recoveryApi.closeProvisionPeriod({ baseDate, label: label || undefined });
            toast.success('Período fechado com sucesso');
            setBaseDate(todayIso());
            setLabel('');
            loadSnapshots();
            onClosed();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 403) {
                toast.error('Apenas o jurídico (ADMIN/LEGAL) pode fechar o período contábil');
            } else if (e instanceof ApiError && e.status === 422) {
                toast.error(e.data?.message || 'Dados inválidos para o fechamento');
            } else {
                toast.error(e?.message || 'Erro ao fechar o período');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleVoid = async (snapshotId: string) => {
        if (!voidReason.trim()) {
            toast.error('Informe a justificativa da anulação');
            return;
        }
        setIsSaving(true);
        try {
            await recoveryApi.voidProvisionSnapshot(snapshotId, voidReason.trim());
            toast.success('Fechamento anulado');
            setVoidingId(null);
            setVoidReason('');
            loadSnapshots();
            onClosed();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 403) {
                toast.error('Apenas o jurídico (ADMIN/LEGAL) pode anular um fechamento');
            } else if (e instanceof ApiError && e.status === 422) {
                toast.error(e.data?.message || 'Não foi possível anular este fechamento');
            } else {
                toast.error(e?.message || 'Erro ao anular o fechamento');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const startVoiding = (snapshotId: string) => {
        setVoidingId(snapshotId);
        setVoidReason('');
    };

    // Exporta as linhas CONGELADAS do fechamento (não o relatório ao vivo). A lista só traz o
    // cabeçalho; as linhas vêm do detalhe, buscado sob demanda.
    const handleExport = async (snapshotId: string) => {
        setExportingId(snapshotId);
        try {
            const detail: ProvisionSnapshotDetail = await recoveryApi.getProvisionSnapshot(snapshotId);
            downloadCsv(buildSnapshotCsv(detail), snapshotCsvFilename(detail));
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao exportar o fechamento');
        } finally {
            setExportingId(null);
        }
    };

    return (
        <Dialog
            id="provision-period-modal"
            show={true}
            onClose={onClose}
            header="Fechamento contábil do CPC 25"
            size="lg"
            body={
                <div className="px-6 py-5 space-y-6">
                    <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            O fechamento congela a provisão de todos os casos na data-base escolhida.
                            Depois de fechado, o número não muda mesmo que a classificação de um caso
                            seja alterada — é o que a auditoria confere.
                        </p>
                        {/* elevated: o Dialog está em z-50, o drawer precisa subir uma camada */}
                        <HelpButton topic="report.snapshot" elevated label="Abrir ajuda: fechamento contábil" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <DateField
                            label="Data-base do fechamento"
                            value={baseDate}
                            onChange={setBaseDate}
                        />
                        <TextEdit
                            label="Rótulo (opcional, ex.: jan25 a jun25)"
                            value={label}
                            onChange={(e: any) => setLabel(e.target.value)}
                        />
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2">
                        O fechamento retrata o estado dos casos <strong>no momento em que é tirado</strong>;
                        a data-base é o período de referência que vai à contabilidade. Não é possível
                        fechar um período que ainda não terminou.
                    </p>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                            Fechamentos anteriores
                        </h4>
                        {snapshots.length === 0 ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                                Nenhum fechamento registrado ainda.
                            </p>
                        ) : (
                            <div className="space-y-1">
                                {snapshots.map((s) => (
                                    <div key={s.id}
                                        className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-900 text-sm space-y-1.5">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="flex items-center gap-2 min-w-0">
                                                {s.voided
                                                    ? <FiSlash className="text-red-500 shrink-0" size={14} />
                                                    : <FiFileText className="text-gray-400 shrink-0" size={14} />}
                                                <span className={`truncate ${s.voided
                                                    ? 'line-through text-gray-400 dark:text-gray-500'
                                                    : 'dark:text-gray-200'}`}>
                                                    {s.label || `Data-base ${fmtDate(s.baseDate)}`}
                                                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                                                        {' '}• ref. {fmtDate(s.baseDate)} • tirado em {fmtDate(s.closedAt)}
                                                        {' '}• {s.totalCount} caso(s) • por {s.closedBy}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="flex items-center gap-2 shrink-0">
                                                <span className={`font-medium ${s.voided
                                                    ? 'line-through text-gray-400 dark:text-gray-500'
                                                    : 'text-red-700 dark:text-red-300'}`}>
                                                    {s.totalProvisionFormatted || 'R$ 0,00'}
                                                </span>
                                                {/* Anulado também exporta: o arquivo sai marcado como anulado. */}
                                                <button type="button" onClick={() => handleExport(s.id)}
                                                    disabled={exportingId !== null}
                                                    aria-label={`Exportar fechamento ${s.label || fmtDate(s.baseDate)}`}
                                                    title="Exportar as linhas congeladas deste fechamento (CSV por caso)"
                                                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 rounded disabled:opacity-50">
                                                    <FiDownload size={12} />
                                                    {exportingId === s.id ? 'Exportando...' : 'Exportar'}
                                                </button>
                                                {!s.voided && voidingId !== s.id && (
                                                    <button type="button" onClick={() => startVoiding(s.id)}
                                                        disabled={isSaving || voidingId !== null}
                                                        title="Anular este fechamento (exige justificativa)"
                                                        className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-700 rounded disabled:opacity-50">
                                                        Anular
                                                    </button>
                                                )}
                                            </span>
                                        </div>

                                        {s.voided && (
                                            <p className="text-xs text-red-600 dark:text-red-400 pl-6">
                                                Anulado por {s.voidedBy} em {fmtDate(s.voidedAt)}
                                                {s.voidReason ? `: ${s.voidReason}` : ''}
                                            </p>
                                        )}

                                        {/* Form de anulação por item. `key` no id remonta e zera o
                                            campo ao trocar de alvo — evita aplicar a justificativa
                                            digitada ao fechamento errado (armadilha já corrigida nas
                                            seções de Depósitos e Decisões, AF-3688). */}
                                        {voidingId === s.id && (
                                            <div key={s.id} className="pl-6 space-y-2">
                                                <TextEdit
                                                    label="Justificativa da anulação"
                                                    value={voidReason}
                                                    onChange={(e: any) => setVoidReason(e.target.value)}
                                                />
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={() => setVoidingId(null)}
                                                        disabled={isSaving}
                                                        className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded">
                                                        Cancelar
                                                    </button>
                                                    <button type="button" onClick={() => handleVoid(s.id)}
                                                        disabled={isSaving || !voidReason.trim()}
                                                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                                                        Confirmar anulação
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            }
            footer={
                <div className="flex gap-2">
                    <button onClick={onClose} disabled={isSaving}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm disabled:opacity-50">
                        Fechar
                    </button>
                    <button onClick={handleClosePeriod} disabled={isSaving || !baseDate}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
                        <FiLock size={14} /> {isSaving ? 'Fechando...' : 'Fechar período'}
                    </button>
                </div>
            }
        />
    );
};

export default ProvisionPeriodModal;
